/**
 * Club social / directory links for funding email and landing page blocks.
 * URLs come from club settings at render time; which links appear is stored on the block.
 */

import { normalizeFundingAppealDirectoryCountrySegment } from "./fundingLandingPages";

/** Marker replaced server-side when sending outreach email (keep in sync with edge `_shared/funding`). */
export const FUNDING_CLUB_SOCIAL_LINKS_ATTR = "data-mixitone-funding-social-links";
export const FUNDING_CLUB_SOCIAL_LINKS_VALUE = "club";

export const FUNDING_SOCIAL_DIRECTORY_ATTR = "data-mixitone-social-directory";
export const FUNDING_SOCIAL_FACEBOOK_ATTR = "data-mixitone-social-facebook";
export const FUNDING_SOCIAL_WEBSITE_ATTR = "data-mixitone-social-website";

export type FundingClubSocialLinkKind = "directory" | "facebook" | "website";

export type FundingClubSocialLinksSelection = {
  directory: boolean;
  facebook: boolean;
  website: boolean;
};

export const DEFAULT_FUNDING_CLUB_SOCIAL_LINKS_SELECTION: FundingClubSocialLinksSelection = {
  directory: true,
  facebook: true,
  website: true,
};

export type FundingClubSocialLink = {
  kind: FundingClubSocialLinkKind;
  href: string;
  label: string;
};

export type FundingClubSocialLinksContext = {
  directoryBaseUrl?: string;
  clubId?: string | null;
  directorySlug?: string | null;
  clubCountry?: string | null;
  listedInDirectory?: boolean | null;
  facebookPage?: string | null;
  webpage?: string | null;
};

/** Which link types the club can show (for editor checkbox disabled state). */
export type FundingClubSocialLinkAvailability = Record<FundingClubSocialLinkKind, boolean>;

export function normalizeFundingClubSocialLinksSelection(
  raw?: Partial<FundingClubSocialLinksSelection> | null,
): FundingClubSocialLinksSelection {
  return {
    directory: raw?.directory ?? true,
    facebook: raw?.facebook ?? true,
    website: raw?.website ?? true,
  };
}

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/, "");
  const p = path.replace(/^\/+/, "");
  return `${b}/${p}`;
}

function normalizeExternalUrl(raw: string | null | undefined): string | null {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** Public club directory listing URL, or null when the club is not listed. */
export function buildFundingClubDirectoryPageUrl(options: {
  directoryBaseUrl: string;
  clubId: string;
  directorySlug?: string | null;
  clubCountry?: string | null;
  listedInDirectory?: boolean | null;
}): string | null {
  if (!options.listedInDirectory) return null;
  const clubId = options.clubId.trim();
  const slug = (options.directorySlug ?? "").trim();
  const pathSegment = slug || clubId;
  if (!pathSegment) return null;
  const country = normalizeFundingAppealDirectoryCountrySegment(options.clubCountry);
  // Trailing slash matches Cloudflare Pages directory index URLs and avoids a brief
  // post-deploy window where the no-slash path SPA-falls-back to the site index.
  return `${joinUrl(options.directoryBaseUrl, `${country}/clubs/${encodeURIComponent(pathSegment)}`)}/`;
}

export function mixitoneDirectoryLogoUrl(directoryBaseUrl: string): string {
  return joinUrl(directoryBaseUrl, "favicon.svg");
}

export function fundingClubSocialLinkAvailability(
  context: FundingClubSocialLinksContext | undefined,
): FundingClubSocialLinkAvailability {
  const directoryBaseUrl = (context?.directoryBaseUrl ?? "").trim();
  const directory =
    Boolean(directoryBaseUrl && context?.clubId) &&
    Boolean(
      buildFundingClubDirectoryPageUrl({
        directoryBaseUrl,
        clubId: context!.clubId!,
        directorySlug: context?.directorySlug,
        clubCountry: context?.clubCountry,
        listedInDirectory: context?.listedInDirectory,
      }),
    );
  return {
    directory,
    facebook: Boolean(normalizeExternalUrl(context?.facebookPage)),
    website: Boolean(normalizeExternalUrl(context?.webpage)),
  };
}

export function resolveFundingClubSocialLinks(
  context: FundingClubSocialLinksContext | undefined,
  selection: FundingClubSocialLinksSelection = DEFAULT_FUNDING_CLUB_SOCIAL_LINKS_SELECTION,
): FundingClubSocialLink[] {
  if (!context) return [];
  const sel = normalizeFundingClubSocialLinksSelection(selection);
  const directoryBaseUrl = (context.directoryBaseUrl ?? "").trim();
  const links: FundingClubSocialLink[] = [];

  if (sel.directory && directoryBaseUrl && context.clubId) {
    const directoryHref = buildFundingClubDirectoryPageUrl({
      directoryBaseUrl,
      clubId: context.clubId,
      directorySlug: context.directorySlug,
      clubCountry: context.clubCountry,
      listedInDirectory: context.listedInDirectory,
    });
    if (directoryHref) {
      links.push({
        kind: "directory",
        href: directoryHref,
        label: "MixItOne club directory",
      });
    }
  }

  if (sel.facebook) {
    const facebook = normalizeExternalUrl(context.facebookPage);
    if (facebook) links.push({ kind: "facebook", href: facebook, label: "Facebook" });
  }

  if (sel.website) {
    const website = normalizeExternalUrl(context.webpage);
    if (website) links.push({ kind: "website", href: website, label: "Website" });
  }

  return links;
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(input: string): string {
  return escapeHtml(input);
}

function svgDataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const FACEBOOK_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img"><path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>`;

const WEBSITE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" role="img"><circle cx="12" cy="12" r="9" stroke="#1d4ed8" stroke-width="1.75"/><path d="M3 12h18M12 3c2.5 2.8 3.8 6.2 3.8 9s-1.3 6.2-3.8 9M12 3c-2.5 2.8-3.8 6.2-3.8 9s1.3 6.2 3.8 9" stroke="#1d4ed8" stroke-width="1.75"/></svg>`;

const FACEBOOK_ICON_DATA_URI = svgDataUri(FACEBOOK_ICON_SVG);
const WEBSITE_ICON_DATA_URI = svgDataUri(WEBSITE_ICON_SVG);

function socialLinkIconSrc(kind: FundingClubSocialLinkKind, directoryBaseUrl: string): string {
  switch (kind) {
    case "directory":
      return mixitoneDirectoryLogoUrl(directoryBaseUrl);
    case "facebook":
      return FACEBOOK_ICON_DATA_URI;
    case "website":
      return WEBSITE_ICON_DATA_URI;
  }
}

export type RenderFundingClubSocialLinksOptions = {
  email?: boolean;
  directoryBaseUrl?: string;
};

function renderSocialLinkAnchor(
  link: FundingClubSocialLink,
  options: RenderFundingClubSocialLinksOptions,
  directoryBaseUrl: string,
): string {
  const href = escapeAttr(link.href);
  const title = escapeAttr(link.label);
  const iconSrc = escapeAttr(socialLinkIconSrc(link.kind, directoryBaseUrl));
  const size = options.email ? 32 : 36;

  if (options.email) {
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" title="${title}" style="display:inline-block;margin:0 10px;vertical-align:middle;text-decoration:none;"><img src="${iconSrc}" alt="${title}" width="${size}" height="${size}" style="display:block;border:0;border-radius:6px;" /></a>`;
  }
  return `<a class="social-link social-link-${link.kind}" href="${href}" target="_blank" rel="noopener noreferrer" title="${title}"><img src="${iconSrc}" alt="" width="${size}" height="${size}" /></a>`;
}

/** Render icon row for email (inline styles) or landing page (class-based). */
export function renderFundingClubSocialLinksHtml(
  links: FundingClubSocialLink[],
  options: RenderFundingClubSocialLinksOptions = {},
): string {
  if (links.length === 0) return "";

  const directoryBaseUrl = (options.directoryBaseUrl ?? "https://directory.mixitone.com").replace(/\/+$/, "");
  const items = links.map((link) => renderSocialLinkAnchor(link, options, directoryBaseUrl)).join("");

  if (options.email) {
    return `<p style="margin:20px 0;text-align:center;line-height:1;">${items}</p>`;
  }
  return items;
}

function selectionFlag(on: boolean): string {
  return on ? "1" : "0";
}

/** Placeholder for stored email HTML — resolved at send or in-editor preview. */
export function fundingClubSocialLinksPlaceholderHtml(
  selection: FundingClubSocialLinksSelection = DEFAULT_FUNDING_CLUB_SOCIAL_LINKS_SELECTION,
): string {
  const sel = normalizeFundingClubSocialLinksSelection(selection);
  return `<div ${FUNDING_CLUB_SOCIAL_LINKS_ATTR}="${FUNDING_CLUB_SOCIAL_LINKS_VALUE}" ${FUNDING_SOCIAL_DIRECTORY_ATTR}="${selectionFlag(sel.directory)}" ${FUNDING_SOCIAL_FACEBOOK_ATTR}="${selectionFlag(sel.facebook)}" ${FUNDING_SOCIAL_WEBSITE_ATTR}="${selectionFlag(sel.website)}"></div>`;
}

export function parseFundingClubSocialLinksSelectionFromAttrs(attribs: {
  getAttribute(name: string): string | null | undefined;
}): FundingClubSocialLinksSelection {
  const read = (name: string, fallback: boolean) => {
    const v = attribs.getAttribute(name);
    if (v == null || v === "") return fallback;
    return v !== "0" && v !== "false";
  };
  return {
    directory: read(FUNDING_SOCIAL_DIRECTORY_ATTR, true),
    facebook: read(FUNDING_SOCIAL_FACEBOOK_ATTR, true),
    website: read(FUNDING_SOCIAL_WEBSITE_ATTR, true),
  };
}

export function fundingClubSocialLinksContextFromClub(
  club: {
    id?: string | null;
    country?: string | null;
    listed_in_directory?: boolean | null;
    directory_slug?: string | null;
    facebook_page?: string | null;
    webpage?: string | null;
  },
  options?: { directoryBaseUrl?: string },
): FundingClubSocialLinksContext {
  return {
    directoryBaseUrl: options?.directoryBaseUrl,
    clubId: club.id ?? null,
    directorySlug: club.directory_slug ?? null,
    clubCountry: club.country ?? null,
    listedInDirectory: club.listed_in_directory ?? false,
    facebookPage: club.facebook_page ?? null,
    webpage: club.webpage ?? null,
  };
}
